import { PAGE_KEYS } from "./page-key";
import { ROLES } from "./role-details";

/**
 * Button Role Permission Configuration
 * ------------------------------------
 * This file defines role-based access for UI buttons across the application.
 *
 * ✅ BUTTON_PERMISSIONS:
 *    - Controls full access to buttons (enabled / visible).
 *    - Supports both generic button types AND component-level button overrides.
 *    - If a component key exists, it takes priority over generic access.
 *
 * ✅ ENABLED_BUTTON_ROLES:
 *    - Add user roles in ENABLED_BUTTON_ROLES which have access of button otherwise button is disabled for remaining user roles.
 *
 * Usage Flow in RoleButton:
 *  1. If role is found under DISABLE_ONLY_BUTTONS → button stays disabled but visible
 *  2. Else if role is allowed under component-level BUTTON_PERMISSIONS → enabled
 *  3. Else if role is allowed under generic BUTTON_PERMISSIONS → enabled
 *  4. Else → hidden
 */

export const BUTTON_TYPES = {
  ADD: "ADD",
  EDIT: "EDIT",
  DELETE: "DELETE",
  SAVE: "SAVE",
  EMAIL: "EMAIL",
  EXPORT: "EXPORT",
  APPROVE: "APPROVE",
  DUPLICATE: "DUPLICATE",
};

export const BUTTON_PERMISSIONS = {
  // Generic permissions
  [BUTTON_TYPES.ADD]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.EDIT]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.DELETE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.SAVE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.EMAIL]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.EXPORT]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.APPROVE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  [BUTTON_TYPES.DUPLICATE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],

  /* -----------------------------------------------------------------------
   * Generic Permissions — currently commented out.
   *
   * These represent a stricter access control configuration where
   * fewer roles have access to certain buttons. Keep for future reference
   * in case permission needs to be tightened or restored to this version.
   *
   * To re-enable, uncomment the desired button mappings.
   * -----------------------------------------------------------------------
  */

  // [BUTTON_TYPES.ADD]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS],
  // [BUTTON_TYPES.EDIT]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS],
  // [BUTTON_TYPES.DELETE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER],
  // [BUTTON_TYPES.SAVE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS],
  // [BUTTON_TYPES.EMAIL]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP],
  // [BUTTON_TYPES.EXPORT]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  // [BUTTON_TYPES.APPROVE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER],

  /* -----------------------------------------------------------------------
   * 🧩 Component-Specific Button Permissions — currently commented out.
   *
   * Use this section to override the generic permissions for specific pages.
   * Each PAGE_KEY maps to a set of BUTTON_TYPES with custom role access.
   * Keep commented for now; enable once component-level role customization
   * is needed for individual pages.
   *
   * Example use cases:
   *   - Restrict ADD/EDIT/DELETE on sensitive pages
   *   - Allow EXPORT only for analysts/viewers on reporting pages
   * -----------------------------------------------------------------------
  */

  // [PAGE_KEYS.VERIFY_BENCHMARK_ARGUS]: {
  //   [BUTTON_TYPES.ADD]: [ROLES.SYSTEM_ADMIN],
  //   [BUTTON_TYPES.EDIT]: [ROLES.SYSTEM_ADMIN],
  //   [BUTTON_TYPES.DELETE]: [ROLES.SYSTEM_ADMIN],
  // },
  // [PAGE_KEYS.RACK_PRICING_REPORT]: {
  //   [BUTTON_TYPES.EXPORT]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  // },
  // [PAGE_KEYS.DAILY_ALLOCATION]: {
  //   [BUTTON_TYPES.EXPORT]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  // },
};

export const ENABLED_BUTTON_ROLES = {
  /* -----------------------------------------------------------------------
   * 🧩 Future Component-Specific Enabled Buttons — currently commented out.
   *
   * This section defines which buttons remain *enabled* for specific roles
   * on certain components/pages even if the button is visible to all.
   *
   * Difference from BUTTON_PERMISSIONS:
   *   - BUTTON_PERMISSIONS → Controls visibility (can see or not)
   *   - ENABLED_BUTTON_ROLES → Controls enabled state (clickable or disabled)
   *
   * Enable this section later if component-level button enablement logic
   * (beyond global permissions) becomes necessary.
   * -----------------------------------------------------------------------
  */

  // [PAGE_KEYS.RACK_PRICING_REPORT]: {
  //   [BUTTON_TYPES.EMAIL]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP],
  // },
  // [PAGE_KEYS.DAILY_ALLOCATION]: {
  //   [BUTTON_TYPES.SAVE]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP, ROLES.OPERATIONS, ROLES.ANALYST, ROLES.VIEWER],
  //   [BUTTON_TYPES.EMAIL]: [ROLES.SYSTEM_ADMIN, ROLES.MANAGER, ROLES.SALES_REP],
  // },
}